/**
 * ChatDialog Component (Refactored)
 * Main orchestrator component using extracted modules
 */

import React, { useState, useEffect, useRef, useMemo, useCallback, forwardRef } from "react";
import {
        Box,
        Button,
        Dialog,
        DialogActions,
        DialogContent,
        DialogContentText,
        DialogTitle as MuiDialogTitle,
        Alert,
        useTheme,
        alpha,
        Drawer,
        useMediaQuery,
} from "@mui/material";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useChatDialog } from "../../contexts/ChatDialogContext";
import { fetchUserContext } from "../../services/chatContextService";

// Custom Hooks
import { useChatSession } from "../../hooks/useChatSession";
import { useChatMessages } from "../../hooks/useChatMessages";
import { useToolExecution } from "../../hooks/useToolExecution";
import { useResponseEnhancement } from "../../hooks/useResponseEnhancement";
import { useChatMessageLimit } from "../../hooks/useChatMessageLimit";

// UI Components
import ChatFab from "./ChatFab";
import ChatDialogHeader from "./ChatDialogHeader";
import ChatHistory from "./ChatHistory";
import ChatMessages from "./ChatMessages";
import ChatInput from "./ChatInput";
import QuickActionsPanel from "./QuickActionsPanel";

import "../../components/grid-components/devicefilter/DeviceFilter.css";

const ChatDialog = forwardRef((props, ref) => {
        const { t } = useTranslation();
        const { currentUser } = useAuth();
        const { chatDialogRef } = useChatDialog();
        const [openDialog, setOpenDialog] = useState(false);
        const [message, setMessage] = useState("");
        const [error, setError] = useState(null);
        const [isHistoryOpen, setIsHistoryOpen] = useState(false);
        const [isQuickActionsExpanded, setIsQuickActionsExpanded] = useState(false);
        const [isFullscreen, setIsFullscreen] = useState(false);
        const [deleteConfirmChatId, setDeleteConfirmChatId] = useState(null);

        const messagesEndRef = useRef(null);
        const theme = useTheme();
        const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
        const mainColor = theme.palette.primary.main;

        const exampleQuestions = useMemo(() => [
                'ai-chat-example-question-1',
                'ai-chat-example-question-2',
                'ai-chat-example-question-3',
                'ai-chat-example-question-4',
                'ai-chat-example-question-5',
                'ai-chat-example-question-6',
                'ai-chat-example-question-7',
                'ai-chat-example-question-8',
        ], []);

        const historyWidth = isHistoryOpen ? 280 : 64;

        // Custom hooks
        const {
                sessions,
                selectedChatId,
                createChat,
                selectChat,
                deleteChat,
                loading: sessionLoading,
        } = useChatSession(currentUser?.uid, openDialog);

        const { enhanceResponse } = useResponseEnhancement(currentUser?.uid);

        const refreshUserContext = useCallback(async () => {
                if (!currentUser?.uid) {
                        return null;
                }
                try {
                        return await fetchUserContext(currentUser.uid);
                } catch (contextError) {
                        console.error("[ChatDialog][refreshUserContext] - Error fetching user context:", contextError);
                        return null;
                }
        }, [currentUser?.uid]);

        const ensureChatSession = useCallback(async () => {
                if (selectedChatId) {
                        return selectedChatId;
                }
                return await createChat();
        }, [selectedChatId, createChat]);

        const {
                messages,
                streamingText,
                isStreaming,
                sendMessage: sendChatMessage,
                loading: messagesLoading,
        } = useChatMessages(currentUser?.uid, selectedChatId, openDialog, enhanceResponse);

        const {
                runTool,
                toolLoadingId,
                toolError,
                setToolError,
                interpretToolResults,
        } = useToolExecution(
                currentUser?.uid,
                selectedChatId,
                ensureChatSession,
                refreshUserContext,
                null, // userContext - will be refreshed in hooks
                enhanceResponse
        );

        const {
                remaining: messagesRemaining,
                limit: messagesLimit,
                isLimited: hasMessageLimit,
                refresh: refreshMessageLimit,
        } = useChatMessageLimit(currentUser?.uid, openDialog);

        const isChatLoading = sessionLoading && messages.length === 0;
        const disableInteractions = messagesLoading || isStreaming || sessionLoading;
        const showSendSpinner = messagesLoading || isStreaming;

        // Scroll to bottom when chat history updates
        useEffect(() => {
                if (messagesEndRef.current) {
                        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
                }
        }, [messages, streamingText]);

        // Handle message sending
        const handleSendMessage = async () => {
                if (!message.trim() || disableInteractions) {
                        return;
                }

                const activeChatId = await ensureChatSession();

                if (!activeChatId) {
                        return;
                }

                const userMessage = message.trim();
                setMessage("");
                await sendChatMessage(userMessage, runTool, interpretToolResults);
                // Refresh message limit after sending
                refreshMessageLimit();
        };

        // Handle example question click
        const handleQuestionClick = useCallback(async (questionKey) => {
                if (disableInteractions) {
                        return;
                }

                const questionText = t(questionKey);
                const activeChatId = await ensureChatSession();

                if (!activeChatId || !questionText) {
                        return;
                }

                await sendChatMessage(questionText, runTool, interpretToolResults);
                // Refresh message limit after sending
                refreshMessageLimit();
        }, [disableInteractions, t, ensureChatSession, sendChatMessage, runTool, interpretToolResults, refreshMessageLimit]);

        // Set ref in context - must be after sendChatMessage is defined
        useEffect(() => {
                if (chatDialogRef?.current !== undefined) {
                        chatDialogRef.current = {
                                open: () => {
                                        setOpenDialog(true);
                                        setError(null);
                                        setToolError(null);
                                },
                                openWithMessage: async (messageText) => {
                                        setOpenDialog(true);
                                        setError(null);
                                        setToolError(null);
                                        
                                        // Create new chat session and send message
                                        const activeChatId = await createChat();
                                        if (activeChatId && messageText) {
                                                // Wait a bit for the chat to initialize
                                                await new Promise(resolve => setTimeout(resolve, 300));
                                                
                                                // Send the message directly
                                                await sendChatMessage(messageText, runTool, interpretToolResults, activeChatId);
                                        }
                                },
                        };
                }
        }, [chatDialogRef, createChat, sendChatMessage, runTool, interpretToolResults, setToolError]);

        // Handle dialog open/close
        const handleOpenDialog = () => {
                setOpenDialog(true);
                setError(null);
                setToolError(null);
        };

        const handleCloseDialog = () => {
                setOpenDialog(false);
                setMessage("");
                setError(null);
                setToolError(null);
                setIsHistoryOpen(false);
                setIsFullscreen(false);
        };

        const handleFullscreenToggle = () => {
                setIsFullscreen((prev) => !prev);
        };

        const handleCreateNewChat = async () => {
                setIsHistoryOpen(true);
                setError(null);
                setToolError(null);
                await createChat();
        };

        const handleDeleteChatWrapper = (chatId) => {
                setDeleteConfirmChatId(chatId);
        };

        const handleConfirmDelete = async () => {
                if (deleteConfirmChatId) {
                        await deleteChat(deleteConfirmChatId);
                }
                setDeleteConfirmChatId(null);
        };

        const handleCancelDelete = () => {
                setDeleteConfirmChatId(null);
        };

        return (
                <>
                        <ChatFab onClick={handleOpenDialog} mainColor={mainColor} />

                        <Dialog
                                open={openDialog}
                                onClose={handleCloseDialog}
                                fullScreen={isMobile || isFullscreen}
                                slotProps={{
                                        backdrop: {
                                                sx: {
                                                        backgroundColor: alpha(theme.palette.common.black, 0.6),
                                                        backdropFilter: "blur(8px)",
                                                },
                                        },
                                }}
                                PaperProps={{
                                        sx: {
                                                borderRadius: (isMobile || isFullscreen) ? 0 : "24px",
                                                width: (isMobile || isFullscreen)
                                                        ? "100%"
                                                        : { xs: "95%", sm: "700px", md: "800px", lg: "900px" },
                                                maxWidth: (isMobile || isFullscreen) ? "100vw" : "95vw",
                                                height: (isMobile || isFullscreen) ? "100%" : "auto",
                                                maxHeight: (isMobile || isFullscreen) ? "100vh" : "92vh",
                                                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                                padding: (isMobile || isFullscreen) ? 0 : "8px",
                                                backdropFilter: "blur(20px)",
                                                background: `linear-gradient(150deg, ${alpha(
                                                        theme.palette.background.paper,
                                                        0.95
                                                )} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
                                                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)",
                                                overflow: "hidden",
                                                display: "flex",
                                                flexDirection: "column",
                                                position: "relative",
                                                "&::before": {
                                                        content: '""',
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        height: "1px",
                                                        background: `linear-gradient(90deg, transparent, ${alpha(
                                                                theme.palette.primary.main,
                                                                0.3
                                                        )}, transparent)`,
                                                },
                                        },
                                }}
                                TransitionProps={{
                                        timeout: 400,
                                }}
                                keepMounted={false}
                        >
                                <ChatDialogHeader
                                        isMobile={isMobile}
                                        selectedChatId={selectedChatId}
                                        disableInteractions={disableInteractions}
                                        onClose={handleCloseDialog}
                                        onDelete={handleDeleteChatWrapper}
                                        onHistoryToggle={() => setIsHistoryOpen(true)}
                                        isFullscreen={isFullscreen}
                                        onFullscreenToggle={handleFullscreenToggle}
                                />

                                {error && (
                                        <Alert severity="error" sx={{ m: 2, mb: 0 }} onClose={() => setError(null)}>
                                                {error}
                                        </Alert>
                                )}

                                <DialogContent
                                        sx={{
                                                display: "flex",
                                                flexDirection: isMobile ? "column" : "row",
                                                padding: theme.spacing(isMobile ? 2 : 3),
                                                gap: theme.spacing(isMobile ? 2 : 2.5),
                                                flex: 1,
                                                minHeight: isMobile ? "calc(100vh - 220px)" : 500,
                                                maxHeight: isMobile ? "unset" : "70vh",
                                                overflow: "hidden",
                                        }}
                                >
                                        {!isMobile && (
                                                <ChatHistory
                                                        sessions={sessions}
                                                        selectedChatId={selectedChatId}
                                                        isOpen={isHistoryOpen}
                                                        variant="sidebar"
                                                        historyWidth={historyWidth}
                                                        onSelectChat={selectChat}
                                                        onDeleteChat={handleDeleteChatWrapper}
                                                        onCreateChat={handleCreateNewChat}
                                                        onToggle={(open) => setIsHistoryOpen(open)}
                                                        disableInteractions={disableInteractions}
                                                />
                                        )}
                                        <Box
                                                sx={{
                                                        flex: 1,
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        p: isMobile ? 0 : 2,
                                                        gap: isMobile ? 1.5 : 2,
                                                        overflow: "hidden",
                                                }}
                                        >
                                                {exampleQuestions.length > 0 && (
                                                        <QuickActionsPanel
                                                                questions={exampleQuestions}
                                                                onQuestionClick={handleQuestionClick}
                                                                disabled={disableInteractions}
                                                                isExpanded={isQuickActionsExpanded}
                                                                onToggle={() => setIsQuickActionsExpanded(!isQuickActionsExpanded)}
                                                        />
                                                )}

                                                {toolError && (
                                                        <Alert
                                                                severity="warning"
                                                                onClose={() => setToolError(null)}
                                                        >
                                                                {toolError}
                                                        </Alert>
                                                )}

                                                <ChatMessages
                                                        messages={messages}
                                                        streamingText={streamingText}
                                                        isStreaming={isStreaming}
                                                        isLoading={isChatLoading}
                                                        messagesEndRef={messagesEndRef}
                                                        mainColor={mainColor}
                                                        onToolClick={(toolId, options) => runTool(toolId, options)}
                                                        toolLoadingId={toolLoadingId}
                                                        isDisabled={disableInteractions}
                                                />
                                        </Box>
                                </DialogContent>
                                <DialogActions
                                        sx={{
                                                padding: theme.spacing(isMobile ? 1.5 : 2, isMobile ? 2 : 3, isMobile ? 2 : 3),
                                                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                                background: `linear-gradient(135deg, transparent 0%, ${alpha(theme.palette.background.default, 0.3)} 100%)`,
                                                display: "flex",
                                                flexDirection: isMobile ? "column" : "row",
                                                gap: theme.spacing(isMobile ? 1.5 : 1),
                                                alignItems: isMobile ? "stretch" : "center",
                                        }}
                                >
                                        <ChatInput
                                                message={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                                onSend={handleSendMessage}
                                                disabled={disableInteractions}
                                                isLoading={showSendSpinner}
                                                messagesRemaining={messagesRemaining}
                                                messagesLimit={messagesLimit}
                                                hasMessageLimit={hasMessageLimit}
                                        />
                                </DialogActions>
                        </Dialog>
                        {isMobile && (
                                <Drawer
                                        anchor="left"
                                        open={isHistoryOpen}
                                        onClose={() => setIsHistoryOpen(false)}
                                        ModalProps={{ keepMounted: true }}
                                        PaperProps={{
                                                sx: {
                                                        width: "100%",
                                                        maxWidth: "100%",
                                                        backgroundColor: alpha(theme.palette.background.default, 0.95),
                                                        backdropFilter: "blur(12px)",
                                                        borderRight: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                                },
                                        }}
                                >
                                        <ChatHistory
                                                sessions={sessions}
                                                selectedChatId={selectedChatId}
                                                isOpen={true}
                                                variant="drawer"
                                                historyWidth={280}
                                                onSelectChat={selectChat}
                                                onDeleteChat={handleDeleteChatWrapper}
                                                onCreateChat={handleCreateNewChat}
                                                onToggle={(open) => setIsHistoryOpen(open)}
                                                disableInteractions={disableInteractions}
                                        />
                                </Drawer>
                        )}

                        {/* Delete confirmation dialog — explicit z-index to stack above the main chat Dialog */}
                        <Dialog
                                open={Boolean(deleteConfirmChatId)}
                                onClose={handleCancelDelete}
                                aria-labelledby="delete-chat-confirm-title"
                                sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
                        >
                                <MuiDialogTitle id="delete-chat-confirm-title">
                                        {t("ai-chat-history-delete-chat")}
                                </MuiDialogTitle>
                                <DialogContent>
                                        <DialogContentText>
                                                {t("ai-chat-history-delete-confirm") || "Are you sure you want to delete this chat?"}
                                        </DialogContentText>
                                </DialogContent>
                                <DialogActions>
                                        <Button onClick={handleCancelDelete}>
                                                {t("cancel") || "Cancel"}
                                        </Button>
                                        <Button onClick={handleConfirmDelete} color="error" variant="contained">
                                                {t("delete") || "Delete"}
                                        </Button>
                                </DialogActions>
                        </Dialog>
                </>
        );
});

ChatDialog.displayName = "ChatDialog";

export default ChatDialog;

